import React, { useMemo } from 'react'
import { Box, Grid, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { DatePicker, TimePicker, DateTimePicker } from '@mui/x-date-pickers'
import Select from 'react-select'
import {
  PMPSectionInputBox,
  PMPSectionTextAreaBox,
  PMPSectionInputLabel,
} from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageStyles'
import { commonStylesPrescriptionSelect, commonStylesPrescriptionSelectWithRequiredParams } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import AsyncSelectField from './AsyncSelectField'
import HybridSelectField from './HybridSelectField'
import EmrSectionActionsGroup from './EmrSectionActionsGroup'
import {
  getEmrFieldValue,
  getFieldValue,
} from 'src/mentor/components/EmrRelatedComponents/utils/fieldValueUtils'
import {
  createEmrFieldValue,
  updateFieldInSubSection,
} from 'src/mentor/components/EmrRelatedComponents/utils/fieldUpdateUtils'

export default function GenericEmrSubSectionComponent({
  subSection,
  subSectionData,
  setSubSectionsData,
  layout = 'column', // 'column' | 'row' | 'grid'
  columns = 2, // Number of columns for grid layout (1-12)
  spacing = 2, // Grid spacing
  practitionerRole = null, // For role-based action filtering
  runtime = {}, // Runtime context for action execution
}) {
  // Get current subsection data
  // console.log({ subSectionData }) // Removed to prevent loop logging
  const currentSubSectionData = subSectionData || {}
  // const currentSubSectionData = useMemo(() => {
  //   return subSectionsData?.find((data) => data?.subSectionCode === subSection?.code)
  // }, [subSectionsData, subSection?.code])

  // Helper: Update field value in subsection data
  const updateFieldValue = (
    fieldCode,
    value,
    fieldType,
    displayValue = null,
    fieldLabel = null,
  ) => {
    setSubSectionsData((currentSubSectionData) => {
      // Create EMR value using utility (DRY principle)
      const emrValue = createEmrFieldValue(value, fieldType, displayValue, fieldLabel)

      // Update field in subsection using utility (DRY principle)
      return updateFieldInSubSection(currentSubSectionData, fieldCode, emrValue, subSection)
    })
  }

  // Helper: Check if field should be shown based on showIf conditions
  const shouldShowField = (field) => {
    if (!field?.showIf || field.showIf.length === 0) return true

    return field.showIf.every((condition) => {
      const dependentFieldValue = getFieldValue({
        fieldCode: condition.fieldCode,
        subSectionDataValues: currentSubSectionData?.values || {},
        fieldType: null,
        subSection,
      })
      const { op, code, codes, stringValue, numberValue, booleanValue } = condition

      switch (op) {
        case 'EQ':
          if (code !== undefined) return dependentFieldValue === code
          if (stringValue !== undefined) return dependentFieldValue === stringValue
          if (numberValue !== undefined) return dependentFieldValue === numberValue
          if (booleanValue !== undefined) return dependentFieldValue === booleanValue
          return false

        case 'NEQ':
          if (code !== undefined) return dependentFieldValue !== code
          if (stringValue !== undefined) return dependentFieldValue !== stringValue
          if (numberValue !== undefined) return dependentFieldValue !== numberValue
          if (booleanValue !== undefined) return dependentFieldValue !== booleanValue
          return false

        case 'IN':
          return codes?.includes(dependentFieldValue)

        case 'NOT_IN':
          return !codes?.includes(dependentFieldValue)

        case 'GT':
          return numberValue !== undefined && dependentFieldValue > numberValue

        case 'GTE':
          return numberValue !== undefined && dependentFieldValue >= numberValue

        case 'LT':
          return numberValue !== undefined && dependentFieldValue < numberValue

        case 'LTE':
          return numberValue !== undefined && dependentFieldValue <= numberValue

        case 'EXISTS':
          return dependentFieldValue != null && dependentFieldValue !== ''

        case 'TRUE':
          return dependentFieldValue === true

        case 'FALSE':
          return dependentFieldValue === false

        default:
          return true
      }
    })
  }

  // Helper: Get options for SELECT fields
  const getFieldOptions = (field) => {
    const optionSource = field?.optionSource

    // STATIC and VALUE_SET: use staticOptions (server hydrates VALUE_SET to staticOptions)
    if (optionSource?.type === 'STATIC' || optionSource?.type === 'VALUE_SET') {
      return optionSource?.staticOptions || []
    }

    // QUERY and HYBRID: handled by specialized components (return empty here)
    if (optionSource?.type === 'QUERY' || optionSource?.type === 'HYBRID') {
      return []
    }

    // Fallback for undefined/null optionSource (backward compatibility)
    return []
  }

  // Render field based on type
  const renderField = (field) => {
    if (!shouldShowField(field)) return null

    const value = getFieldValue({
      fieldCode: field.code,
      fieldType: field.type,
      subSectionDataValues: currentSubSectionData?.values || {},
      subSection,
    })

    switch (field.type) {
      case 'TEXT':
        return (
          <div
            key={field.code}
            style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
          >
            <PMPSectionInputLabel>{field.label}</PMPSectionInputLabel>
            <PMPSectionInputBox
              value={value}
              onChange={(e) =>
                updateFieldValue(field.code, e.target.value, field.type, null, field.label)
              }
              placeholder={field.placeholder}
              disabled={field.readOnly}
              required={field.required}
              minLength={field.minLength}
              maxLength={field.maxLength}
              pattern={field.regex}
            />
          </div>
        )

      case 'TEXTAREA':
        return (
          <div
            key={field.code}
            style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
          >
            <PMPSectionInputLabel>{field.label}</PMPSectionInputLabel>
            <PMPSectionTextAreaBox
              value={value}
              onChange={(e) =>
                updateFieldValue(field.code, e.target.value, field.type, null, field.label)
              }
              placeholder={field.placeholder}
              disabled={field.readOnly}
              required={field.required}
              minLength={field.minLength}
              maxLength={field.maxLength}
              minRows={1}
            />
          </div>
        )

      case 'NUMBER':
        return (
          <div
            key={field.code}
            style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
          >
            <PMPSectionInputLabel>{field.label}</PMPSectionInputLabel>
            <div style={{ position: 'relative', width: '100%' }}>
              <PMPSectionInputBox
                style={{ maxWidth: 'unset' }}
                type="number"
                value={value}
                onChange={(e) =>
                  updateFieldValue(field.code, e.target.value, field.type, null, field.label)
                }
                placeholder={field.placeholder}
                disabled={field.readOnly}
                required={field.required}
                min={field.minNumber}
                max={field.maxNumber}
              />
              {field.unit && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  {field.unit}
                </Typography>
              )}
            </div>
          </div>
        )

      case 'BOOLEAN':
        return (
          <FormControlLabel
            key={field.code}
            control={
              <Checkbox
                checked={value}
                onChange={(e) =>
                  updateFieldValue(field.code, e.target.checked, field.type, null, field.label)
                }
                disabled={field.readOnly}
              />
            }
            label={field.label}
          />
        )

      case 'DATE':
        return (
          <div
            key={field.code}
            style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
          >
            <PMPSectionInputLabel>{field.label}</PMPSectionInputLabel>
            <DatePicker
              value={value}
              onChange={(newValue) =>
                updateFieldValue(field.code, newValue, field.type, null, field.label)
              }
              disabled={field.readOnly}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: field.required,
                  helperText: field.helpText,
                },
              }}
            />
          </div>
        )

      case 'TIME':
        return (
          <div
            key={field.code}
            style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
          >
            <PMPSectionInputLabel>{field.label}</PMPSectionInputLabel>
            <TimePicker
              value={value}
              onChange={(newValue) =>
                updateFieldValue(field.code, newValue, field.type, null, field.label)
              }
              disabled={field.readOnly}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: field.required,
                  helperText: field.helpText,
                },
              }}
            />
          </div>
        )

      case 'DATETIME':
        return (
          <div
            key={field.code}
            style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
          >
            <PMPSectionInputLabel>{field.label}</PMPSectionInputLabel>
            <DateTimePicker
              value={value}
              onChange={(newValue) =>
                updateFieldValue(field.code, newValue, field.type, null, field.label)
              }
              disabled={field.readOnly}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: field.required,
                  helperText: field.helpText,
                },
              }}
            />
          </div>
        )

      case 'SELECT_ONE': {
        const optionSource = field?.optionSource

        // Route to HYBRID component
        if (optionSource?.type === 'HYBRID') {
          return (
            <HybridSelectField
              key={field.code}
              field={field}
              value={getEmrFieldValue({
                fieldCode: field.code,
                subSectionDataValues: currentSubSectionData?.values || {},
              })}
              onChange={(code, display) =>
                updateFieldValue(field.code, code, field.type, display, field.label)
              }
              isMulti={false}
            />
          )
        }

        // Route to QUERY component
        if (optionSource?.type === 'QUERY') {
          return (
            <AsyncSelectField
              key={field.code}
              field={field}
              value={getEmrFieldValue({
                fieldCode: field.code,
                subSectionDataValues: currentSubSectionData?.values || {},
              })}
              onChange={(code, display) =>
                updateFieldValue(field.code, code, field.type, display, field.label)
              }
              isMulti={false}
            />
          )
        }

        // Existing STATIC/VALUE_SET logic
        const options = getFieldOptions(field)
        const selectOptions = options.map((opt) => ({
          value: opt.code,
          label: opt.display || opt.code,
        }))
        const selectedOption = selectOptions.find((opt) => opt.value === value)

        return (
          <div
            key={field.code}
            style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
          >
            <PMPSectionInputLabel>{field.label}</PMPSectionInputLabel>
            <div style={{ width: '100%' }}>
              <Select
                options={selectOptions}
                value={selectedOption}
                onChange={(selected) => {
                  updateFieldValue(
                    field.code,
                    selected?.value,
                    field.type,
                    selected?.label,
                    field.label,
                  )
                }}
                placeholder={field.placeholder || 'Select...'}
                isDisabled={field.readOnly}
                isClearable
                styles={commonStylesPrescriptionSelectWithRequiredParams(field.required, !!value)}
              />
              {field.helpText && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {field.helpText}
                </Typography>
              )}
            </div>
          </div>
        )
      }

      case 'SELECT_MULTI': {
        const optionSource = field?.optionSource

        // Route to HYBRID component
        if (optionSource?.type === 'HYBRID') {
          return (
            <HybridSelectField
              key={field.code}
              field={field}
              value={value}
              onChangeMulti={(codes, items) => {
                // For SELECT_MULTI, updateFieldValue expects array of EmrValueItem objects
                updateFieldValue(field.code, items, field.type, null, field.label)
              }}
              isMulti={true}
            />
          )
        }

        // Route to QUERY component
        if (optionSource?.type === 'QUERY') {
          return (
            <AsyncSelectField
              key={field.code}
              field={field}
              value={value}
              onChangeMulti={(codes, items) => {
                updateFieldValue(field.code, items, field.type, null, field.label)
              }}
              isMulti={true}
            />
          )
        }

        // Existing STATIC/VALUE_SET logic
        const options = getFieldOptions(field)
        const selectOptions = options.map((opt) => ({
          value: opt.code,
          label: opt.display || opt.code,
        }))
        const selectedOptions = selectOptions.filter((opt) => value?.includes(opt.value))

        return (
          <div
            key={field.code}
            style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
          >
            <PMPSectionInputLabel>{field.label}</PMPSectionInputLabel>
            <div style={{ width: '100%', maxWidth: '700px' }}>
              <Select
                isMulti
                options={selectOptions}
                value={selectedOptions}
                onChange={(selected) => {
                  const codes = selected ? selected.map((opt) => opt.value) : []
                  updateFieldValue(field.code, codes, field.type, null, field.label)
                }}
                placeholder={field.placeholder || 'Select...'}
                isDisabled={field.readOnly}
                isClearable
                styles={commonStylesPrescriptionSelectWithRequiredParams(
                  field.required,
                  Array.isArray(value) && value.length > 0,
                )}
              />
              {field.helpText && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {field.helpText}
                </Typography>
              )}
            </div>
          </div>
        )
      }

      default:
        return null
    }
  }

  // Sort fields by sort property
  const sortedFields = useMemo(() => {
    return subSection?.fields || []
    // return [...(subSection?.fields || [])].sort((a, b) => (a.sort || 0) - (b.sort || 0))
  }, [subSection?.fields])

  if (!subSection) return null

  // Render fields based on layout type
  const renderFields = () => {
    if (layout === 'grid') {
      return (
        <Grid container spacing={spacing}>
          {sortedFields.map((field) => {
            const fieldElement = renderField(field)
            if (!fieldElement) return null

            return (
              <Grid item xs={12} sm={12 / columns} md={12 / columns} key={field.code}>
                {fieldElement}
              </Grid>
            )
          })}
        </Grid>
      )
    }

    if (layout === 'row') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: spacing, flexWrap: 'wrap' }}>
          {sortedFields.map((field) => {
            const fieldElement = renderField(field)
            if (!fieldElement) return null

            return (
              <Box key={field.code} sx={{ flex: '1 1 auto', minWidth: '200px' }}>
                {fieldElement}
              </Box>
            )
          })}
        </Box>
      )
    }

    // Default: column layout
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sortedFields.map((field) => renderField(field))}
      </Box>
    )
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Subsection-level actions */}
      {subSection?.actions && subSection.actions.length > 0 && (
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e5e7eb' }}>
          <EmrSectionActionsGroup
            actions={subSection.actions}
            scope="item"
            context={{
              item: subSectionData,
              sectionData: subSectionData,
            }}
            practitionerRole={practitionerRole}
            runtime={runtime}
            sectionData={subSectionData}
            setSectionData={setSubSectionsData}
          />
        </Box>
      )}
      <Typography variant="h6" sx={{ mb: 2 }}>
        {subSection.title}
      </Typography>
      {renderFields()}
    </Box>
  )
}
